import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { User, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';

// Roles alignés avec les constantes du projet Flutter (voir AppConstants.roleAdmin, etc.).
export type Role = 'admin' | 'responsible' | 'teacher' | 'cgs' | 'drh' | 'UNKNOWN';

interface AuthContextValue {
  user: User | null;
  role: Role;
  loading: boolean;
  signOutUser: () => Promise<void>;
  displayName: string;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<Role>('UNKNOWN');
  const [loading, setLoading] = useState(true);
  const [displayName, setDisplayName] = useState<string>('');

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      setUser(firebaseUser);
      setDisplayName('');

      if (firebaseUser) {
        try {
          const userRef = doc(db, 'users', firebaseUser.uid);
          const snap = await getDoc(userRef);
          const data = snap.data() as
            | { role?: string; firstName?: string; lastName?: string }
            | undefined;
          const docRole = data?.role;

          if (
            docRole === 'admin' ||
            docRole === 'responsible' ||
            docRole === 'teacher' ||
            docRole === 'cgs' ||
            docRole === 'drh'
          ) {
            setRole(docRole);
          } else {
            setRole('UNKNOWN');
          }

          const fullName = `${data?.firstName ?? ''} ${data?.lastName ?? ''}`
            .trim();
          if (fullName) {
            setDisplayName(fullName);
          } else if (firebaseUser.displayName) {
            setDisplayName(firebaseUser.displayName);
          } else if (firebaseUser.email) {
            setDisplayName(firebaseUser.email);
          } else {
            setDisplayName('');
          }
        } catch {
          setRole('UNKNOWN');
          setDisplayName(
            firebaseUser?.displayName || firebaseUser?.email || ''
          );
        }
      } else {
        setRole('UNKNOWN');
        setDisplayName('');
      }

      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      role,
      loading,
      signOutUser: () => signOut(auth),
      displayName,
    }),
    [user, role, loading, displayName]
  );

  return (
    <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
};

